import { useEffect, useState } from "react";
import { api, type Question } from "./api";

type EngagementState = {
  saved: boolean;
  reactionType: "consider" | "resonate" | null;
  reactions: { consider: number; resonate: number };
};

const emptyState: EngagementState = { saved: false, reactionType: null, reactions: { consider: 0, resonate: 0 } };

function BookmarkIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3.5h12v17l-6-3.7-6 3.7v-17Z" /></svg>;
}

function ConsiderIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5A8.5 8.5 0 0 0 12 3.5Z" /><path d="M9.7 9.4a2.45 2.45 0 1 1 3.75 2.1c-.9.55-1.45 1-1.45 2.15" /><path d="M12 17.1h.01" /></svg>;
}

function ResonateIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.2 4.6 13A4.8 4.8 0 0 1 11.4 6L12 6.7l.6-.7A4.8 4.8 0 0 1 19.4 13L12 20.2Z" /></svg>;
}

export function QuestionEngagement({ questionId }: { questionId: string }) {
  const [state, setState] = useState<EngagementState | null>(null);
  const [busy, setBusy] = useState<"save" | "consider" | "resonate" | null>(null);
  useEffect(() => {
    let alive = true;
    void api<EngagementState>(`/api/questions/${questionId}/save`).then((value) => { if (alive) setState(value); }).catch(() => { if (alive) setState(emptyState); });
    return () => { alive = false; };
  }, [questionId]);
  async function save() {
    const current = state || emptyState;
    setBusy("save");
    try { setState(await api<EngagementState>(`/api/questions/${questionId}/save`, { method: current.saved ? "DELETE" : "PUT", body: "{}" })); }
    finally { setBusy(null); }
  }
  async function react(type: "consider" | "resonate") {
    const current = state || emptyState;
    setBusy(type);
    try {
      setState(await api<EngagementState>(`/api/questions/${questionId}/reaction`, {
        method: current.reactionType === type ? "DELETE" : "PUT",
        body: current.reactionType === type ? "{}" : JSON.stringify({ reactionType: type }),
      }));
    } finally { setBusy(null); }
  }
  const current = state || emptyState;
  return <div className="question-engagement" aria-label="問いへの反応">
    <button type="button" className={current.saved ? "engagement-button selected" : "engagement-button"} aria-label={current.saved ? "保存を外す" : "この問いを保存"} title={current.saved ? "保存を外す" : "保存"} disabled={busy !== null} onClick={() => void save()}><BookmarkIcon /></button>
    <button type="button" className={current.reactionType === "consider" ? "engagement-button selected" : "engagement-button"} aria-label="考えてみたい" title="考えてみたい" disabled={busy !== null} onClick={() => void react("consider")}><ConsiderIcon /><b>{current.reactions.consider}</b></button>
    <button type="button" className={current.reactionType === "resonate" ? "engagement-button selected" : "engagement-button"} aria-label="共鳴した" title="共鳴した" disabled={busy !== null} onClick={() => void react("resonate")}><ResonateIcon /><b>{current.reactions.resonate}</b></button>
  </div>;
}

export function SavedQuestions({ onOpen }: { onOpen: (question: Question) => void }) {
  const [questions, setQuestions] = useState<Question[] | null>(null);
  useEffect(() => { void api<{ questions: Question[] }>("/api/me/saved-questions").then((value) => setQuestions(value.questions)).catch(() => setQuestions([])); }, []);
  if (!questions) return null;
  return <section className="panel saved-questions"><div className="section-heading"><div><span className="section-kicker">SAVED</span><h2>あとで答える問い</h2></div><span>{questions.length}</span></div>{questions.length ? <div className="saved-question-list">{questions.map((question) => <button type="button" key={question.id} onClick={() => onOpen(question)}><span>{question.tags.map((tag) => `#${tag}`).join(" ")}</span><b>{question.body}</b><small>{question.answered ? "結果を見る" : "答える"}　›</small></button>)}</div> : <p className="empty">気になる問いをブックマークすると、ここで読めます。</p>}</section>;
}
